// League settings' Divisions section (PLAN-2026-10-29-divisions §5, scenario
// B4): how many, each one's name, division games first, the playoff seeding,
// and who is in each division.
//
// TWO KINDS OF SAVE, said on screen. The divisions themselves are rules: they
// change in the draft and go with Save, as a version. Where a team plays is
// not a rule but the team's own (`teams.division_id`), so its menu saves at
// once, as Yahoo's Edit divisions does, and only among the divisions already
// saved: a division in the draft has no id the server knows yet. A save that
// changes which divisions there are splits every team evenly again (B-561),
// and the section says so before Save rather than after.

import { useState } from 'react';
import {
  divisionCounts,
  divisionsFor,
  playsTheField,
  sameDivisions,
  type LeagueSettings,
  type RulesTeam,
} from '@fsp/domain';
import { Caption, Choice, Text, TextField, Toggle, tell } from '../../kit/ui';
import type { VoteOutcome } from '../../data/rules';

export function DivisionsSection({
  draft: d,
  saved,
  set,
  disabled,
  teams,
  canPlace,
  onPlace,
  voting,
}: {
  draft: LeagueSettings;
  /** The rules in force: the divisions a team can be put in now. */
  saved: LeagueSettings;
  set: (mutate: (s: LeagueSettings) => void) => void;
  disabled: boolean;
  /** Every team in seat order with its division (`LeagueRulesView.teams`). */
  teams: readonly RulesTeam[];
  /** A commissioner, before the season opens, in a league that has teams to place. */
  canPlace: boolean;
  onPlace: (teamId: string, divisionId: string | null) => Promise<VoteOutcome>;
  /** The divisions in the rule change up for the league's vote, or null when none is. */
  voting: LeagueSettings['schedule']['divisions'] | null;
}) {
  const divisions = d.schedule.divisions;
  const counts = divisionCounts(d.identity.teamCount);
  // A league stored with a count the editor no longer offers still reads it.
  const offered = counts.includes(divisions.length) ? counts : [...counts, divisions.length];
  const field = playsTheField(d.format.id);
  const kept = saved.schedule.divisions;
  // The draft's names over the saved ids, so a rename reads in the menus too.
  const nameOf = (id: string) =>
    divisions.find((x) => x.id === id)?.name ?? kept.find((x) => x.id === id)?.name ?? id;
  const resplit = !sameDivisions(kept, divisions);
  const [moving, setMoving] = useState<string | null>(null);

  const place = async (team: RulesTeam, divisionId: string) => {
    setMoving(team.id);
    const out = await onPlace(team.id, divisionId);
    setMoving(null);
    if (out.ok) tell(`${team.name} moved to ${nameOf(divisionId)}.`, 'good');
    else tell(out.message, 'bad');
  };

  return (
    <div className="fsp-form-fields">
      <Choice
        label="Divisions"
        description="The standings group by division, division record can break ties, and the playoffs can seed division winners first."
        searchable={false}
        required
        value={String(divisions.length)}
        onChange={(v) =>
          v !== null &&
          set((s) => {
            s.schedule.divisions = divisionsFor(Number(v), s.schedule.divisions);
            // DIVISION WINNERS FIRST needs divisions; with none it seeds by record.
            if (s.schedule.divisions.length === 0) s.playoffs.seeding = 'standings';
          })
        }
        options={offered.map((n) => ({
          value: String(n),
          label: n === 0 ? 'None' : n === 1 ? 'One division' : `${n} divisions`,
        }))}
        disabled={disabled}
      />
      {divisions.length > 0 ? (
        <div className="fsp-form-names">
          {divisions.map((div, i) => (
            <TextField
              key={div.id}
              label={`Division ${i + 1}`}
              value={div.name}
              maxLength={30}
              onChange={(v) => set((s) => (s.schedule.divisions[i]!.name = v))}
              disabled={disabled}
            />
          ))}
        </div>
      ) : null}
      {divisions.length > 0 ? (
        <>
          <Toggle
            label="Division games first"
            description={
              field
                ? 'This format plays the whole league each week, so there are no division games to put first.'
                : 'The weeks with the most division games open the season.'
            }
            on={d.schedule.divisionGamesFirst && !field}
            onChange={(on) => set((s) => (s.schedule.divisionGamesFirst = on))}
            disabled={disabled || field}
          />
          <Choice
            label="Playoff seeding"
            description={
              d.playoffs.teamCount === 0
                ? 'This league has no playoffs.'
                : 'Division winners first gives each division’s leader a playoff place and a top seed, ahead of better records from other divisions.'
            }
            searchable={false}
            required
            value={d.playoffs.seeding}
            onChange={(v) =>
              v !== null &&
              set((s) => (s.playoffs.seeding = v as LeagueSettings['playoffs']['seeding']))
            }
            options={[
              { value: 'standings', label: 'By record' },
              { value: 'division-winners-first', label: 'Division winners first' },
            ]}
            disabled={disabled || d.playoffs.teamCount === 0}
          />
        </>
      ) : null}
      {divisions.length > 0 || kept.length > 0 ? (
        <Placement
          teams={teams}
          kept={kept}
          resplit={resplit}
          adding={kept.length === 0}
          removing={divisions.length === 0}
          canPlace={canPlace}
          votingResplit={voting !== null && !sameDivisions(kept, voting)}
          moving={moving}
          nameOf={nameOf}
          onPick={(team, id) => void place(team, id)}
        />
      ) : null}
    </div>
  );
}

/** Who is in each division: a menu beside every team, saved as it is picked. */
function Placement({
  teams,
  kept,
  resplit,
  adding,
  removing,
  canPlace,
  votingResplit,
  moving,
  nameOf,
  onPick,
}: {
  teams: readonly RulesTeam[];
  kept: LeagueSettings['schedule']['divisions'];
  resplit: boolean;
  adding: boolean;
  removing: boolean;
  canPlace: boolean;
  /** The change up for a vote changes which divisions there are. */
  votingResplit: boolean;
  moving: string | null;
  nameOf: (id: string) => string;
  onPick: (team: RulesTeam, divisionId: string) => void;
}) {
  // SAID BEFORE SAVE (CLAUDE.md, say what will happen): what the change does
  // to where the teams are, while the draft's divisions are not the saved
  // ones; "made" rather than "saved", since in a league that votes it is made
  // when the vote passes.
  const before = removing
    ? 'When this change is made, every team is taken out of its division.'
    : adding
      ? 'When this change is made, the teams are split evenly between the divisions. You can move any team after.'
      : resplit
        ? 'Changing how many divisions there are splits the teams evenly again when the change is made. You can move any team after.'
        : null;
  const counts = new Map<string, number>();
  for (const t of teams)
    if (t.divisionId !== null) counts.set(t.divisionId, (counts.get(t.divisionId) ?? 0) + 1);
  return (
    <div className="fsp-form-fields">
      <Caption>Who is in each division</Caption>
      {before !== null ? (
        <Text size="sm" tone="secondary" block>
          {before}
        </Text>
      ) : (
        <>
          {votingResplit ? (
            <Text size="sm" tone="warn" block>
              The rule change up for a vote changes the divisions: if it passes, every team is split
              evenly again, wherever it has been moved.
            </Text>
          ) : null}
          <Text size="sm" tone="secondary" block>
            {kept.map((k) => `${nameOf(k.id)} ${counts.get(k.id) ?? 0}`).join(' · ')}
            {canPlace ? '. A team’s division is saved as soon as you pick it.' : ''}
          </Text>
          <div className="fsp-form-placed">
            {teams.map((team) => (
              <div key={team.id} className="fsp-form-placed-row">
                <span className="fsp-form-placed-name">{team.name}</span>
                <Choice
                  label={`${team.name}’s division`}
                  hideLabel
                  searchable={false}
                  placeholder="No division"
                  value={team.divisionId}
                  onChange={(v) => v !== null && v !== team.divisionId && onPick(team, v)}
                  options={kept.map((k) => ({ value: k.id, label: nameOf(k.id) }))}
                  disabled={!canPlace || moving !== null}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
